import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import QuickActions from "../components/QuickActions";
import { useLang } from "../i18n/LanguageContext";

const ease = [0.16, 1, 0.3, 1];

export default function MerciPage() {
  const { lang } = useLang();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    document.title =
      lang === "fr"
        ? "Merci — Demande reçue · LAKA"
        : "Thank you — Request received · LAKA";
  }, [lang]);

  const isFR = lang === "fr";

  return (
    <div className="bg-[#F7F7F9] text-[#0A0A0A] min-h-screen antialiased selection:bg-[#D35400] selection:text-white">
      <Header />

      <main className="pt-32 md:pt-40 pb-24 md:pb-36">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="flex items-center gap-3 mb-10"
          >
            <CheckCircle2 size={20} className="text-[#D35400]" />
            <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#D35400]">
              {isFR ? "Demande reçue" : "Request received"} / 200 OK
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 0.1 }}
            className="font-display font-light uppercase tracking-[-0.02em] leading-[0.95] text-[clamp(2.75rem,7vw,7rem)] max-w-[18ch]"
          >
            {isFR ? (
              <>
                <span className="text-[#DC2626]">M</span>erci
                <span className="text-[#D35400]">.</span>
              </>
            ) : (
              <>
                <span className="text-[#DC2626]">T</span>hank you
                <span className="text-[#D35400]">.</span>
              </>
            )}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 0.2 }}
            className="mt-12 max-w-[58ch] space-y-5 text-lg md:text-xl font-light leading-relaxed"
          >
            <p>
              {isFR
                ? "Merci pour votre message."
                : "Thank you for your message."}
            </p>
            <p>
              {isFR
                ? "Nous reviendrons vers vous dans les meilleurs délais pour échanger sur votre projet — architecture, BIM / Revit, dossier AT, rénovation ou aménagement de bureaux."
                : "We will get back to you as soon as possible to discuss your project — architecture, BIM / Revit, technical files, renovation or office layout."}
            </p>
            <p className="font-mono text-sm tracking-[0.18em] uppercase text-[#5C5C5C] pt-2">
              {isFR
                ? "Réponse sous 48h ouvrées"
                : "Reply within 48 working hours"}
            </p>
          </motion.div>

          {/* Quick actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease, delay: 0.35 }}
            className="mt-14 md:mt-20 border-t border-[#E5E5E5] pt-10"
          >
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-6">
              {isFR ? "Contact direct" : "Direct contact"}
            </div>
            <QuickActions variant="inline" source="merci" />
          </motion.div>

          {/* Back to home */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease, delay: 0.5 }}
            className="mt-16 md:mt-24"
          >
            <Link
              to="/"
              data-testid="merci-back-home"
              className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] hover:text-[#D35400] transition-colors"
            >
              <ArrowLeft size={12} />
              {isFR ? "Retour à l'accueil" : "Back to home"}
            </Link>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
